// Where /claim sends the buyer. `next` is attacker-influenceable: only a
// same-origin path is followed (a leading "/", never "//", and the resolved URL
// must stay on our origin, which also rules out "/\evil.com"). Anything else
// falls back to the default.

export const CLAIM_DEFAULT_NEXT = '/onboarding';

// On /signup: the visitor chose to open an account under another email (the
// login page's "Sign up" link carries it once a purchase email is shown), so the
// purchase email is neither prefilled nor sent back to log in.
export const OTHER_EMAIL_PARAM = 'other';

// Tally prefill params the landing pages already send (existing contract).
const PASS_THROUGH = ['name', 'phone_number'] as const;

export function safeNextUrl(raw: string | null, baseUrl: string, fallback: string): URL {
  const base = new URL(baseUrl);
  if (raw && raw.startsWith('/') && !raw.startsWith('//')) {
    try {
      const url = new URL(raw, base);
      if (url.origin === base.origin) return url;
    } catch {
      // fall through to the default
    }
  }
  return new URL(fallback, base);
}

/**
 * The redirect for a visitor who is not signed in: `next` with the purchase
 * email (Stripe's when confirmed, else the one in the link) and the prefill
 * params carried over.
 */
export function claimRedirectUrl(
  params: URLSearchParams,
  baseUrl: string,
  confirmedEmail: string | null
): URL {
  const url = safeNextUrl(params.get('next'), baseUrl, CLAIM_DEFAULT_NEXT);
  const email = confirmedEmail || params.get('email');
  if (email) url.searchParams.set('email', email);
  for (const key of PASS_THROUGH) {
    const value = params.get(key);
    if (value) url.searchParams.set(key, value);
  }
  return url;
}

/**
 * The redirect for a purchase email that already has a login (the purchase made
 * one, or they signed up before): /login with the email prefilled. An explicit
 * `next` rides along as Clerk's redirect_url; the Tally params are dropped (the
 * onboarding form reads the account after login).
 */
export function loginRedirectUrl(params: URLSearchParams, baseUrl: string, email: string): URL {
  const url = new URL('/login', baseUrl);
  url.searchParams.set('email', email);
  if (params.get('next')) {
    const next = safeNextUrl(params.get('next'), baseUrl, CLAIM_DEFAULT_NEXT);
    url.searchParams.set('redirect_url', `${next.pathname}${next.search}`);
  }
  return url;
}

/**
 * The redirect for a purchase email with no login yet: /signup with the email
 * and the prefill params. Not `next` itself: a signed-out visit to the app root
 * (the Sales API default) lands on /login and drops the email. An explicit
 * `next` rides along as Clerk's redirect_url; /signup sends on to log in if
 * the purchase login has appeared by then.
 */
export function signupRedirectUrl(params: URLSearchParams, baseUrl: string, email: string): URL {
  const url = new URL('/signup', baseUrl);
  url.searchParams.set('email', email);
  for (const key of PASS_THROUGH) {
    const value = params.get(key);
    if (value) url.searchParams.set(key, value);
  }
  if (params.get('next')) {
    const next = safeNextUrl(params.get('next'), baseUrl, CLAIM_DEFAULT_NEXT);
    url.searchParams.set('redirect_url', `${next.pathname}${next.search}`);
  }
  return url;
}
